import i18next from 'i18next';
import { toast } from 'sonner';
import { useScenarioTimelineStore } from '../../store/scenario-timeline.store.js';

export class ScenarioTimelineIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async fetchItems(page = 1, pageSize = 10, search = '', sortBy = 'name', sortOrder = 'asc', filters = {}) {
        return this._fetch({ page, pageSize, search, sortBy, sortOrder, ...filters });
    }

    async fetchAll(params = {}) {
        try {
            const response = await this.service.getAll(params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) {
            return { success: false, data: [], error: error.message };
        }
    }

    async _fetch(params) {
        const store = useScenarioTimelineStore.getState();
        store.setLoading(true);
        store.clearError?.();

        try {
            const response = await this.service.getAll(params);
            store.setItems(response.data);

            const pagination = response.pagination || response.meta;
            if (pagination) {
                const currentPage = parseInt(pagination.page || pagination.currentPage || 1);
                const totalPages = parseInt(pagination.totalPages || 1);
                store.setPaginationData({
                    currentPage,
                    itemsPerPage: parseInt(pagination.pageSize || pagination.itemsPerPage || 10),
                    totalItems: parseInt(pagination.total || pagination.totalItems || 0),
                    totalPages,
                    hasNextPage: currentPage < totalPages,
                    hasPreviousPage: currentPage > 1
                });
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = error.message || i18next.t('integrationToasts:anErrorOccurred', { defaultValue: 'An error occurred' });
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async create(data) {
        return this._execute(async () => this.service.create(data));
    }

    async update(id, data) {
        return this._execute(async () => this.service.update(id, data));
    }

    async delete(id) {
        return this._execute(async () => this.service.delete(id));
    }

    async batchDelete(ids) {
        return this._executeBatch(async () => this.service.bulkDelete(ids));
    }

    async _execute(operation) {
        const store = useScenarioTimelineStore.getState();
        store.setLoading(true);
        try {
            const response = await operation();
            toast.success(i18next.t('integrationToasts:operationSuccessful', { defaultValue: 'Operation successful' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = error.message || i18next.t('integrationToasts:anErrorOccurred', { defaultValue: 'An error occurred' });
            toast.error(message);
            store.setError(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async _executeBatch(operation) {
        const store = useScenarioTimelineStore.getState();
        store.setLoading(true);
        try {
            const response = await operation();
            const count = response.deletedCount ?? response.data?.deletedCount;
            toast.success(count !== undefined
                ? i18next.t('integrationToasts:itemsDeletedSuccessfully', {
                    count,
                    defaultValue: '{{count}} items deleted successfully',
                })
                : i18next.t('integrationToasts:itemsDeleted', { defaultValue: 'Items deleted' }));
            return { success: true, deletedCount: count };
        } catch (error) {
            const message = error.message || i18next.t('integrationToasts:anErrorOccurred', { defaultValue: 'An error occurred' });
            toast.error(message);
            store.setError(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }
}
